import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ResponsiveDialog from '../common/ResponsiveDialog';
import type { Cir, CirSaveInput, E2IConfig, Job } from '../../types/e2i';
import { ATTACHMENT_ACCEPT, MAX_ATTACHMENT_BYTES, cleanDateInput, dateOrNull, errorText, formatAmount, round2 } from '../../utils/e2i';

interface Props {
  open: boolean;
  job: Job;
  /** The request being edited; null for a new one. */
  cir: Cir | null;
  /** Defaults for a new request: the SAP customer's VAT number and the client's PO. */
  defaultTrn: string | null;
  defaultPoRef: string | null;
  vatRate: number;
  config: E2IConfig;
  currency: string;
  onClose: () => void;
  onSave: (input: CirSaveInput, files: File[]) => Promise<void>;
}

/** A client invoice request: whole job lines not invoiced yet, the customer's VAT number and PO, the service period. */
const CirDialog: React.FC<Props> = ({ open, job, cir, defaultTrn, defaultPoRef, vatRate, config, currency, onClose, onSave }) => {
  const [picked, setPicked] = useState<number[]>([]);
  const [trn, setTrn] = useState('');
  const [poRef, setPoRef] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPicked(cir ? cir.lines.map((l) => l.jobLineId) : []);
    setTrn(cir ? cir.customerTrn || '' : defaultTrn || '');
    setPoRef(cir ? cir.customerPoRef || '' : defaultPoRef || '');
    setPeriodFrom(cir?.periodFrom ? cir.periodFrom.slice(0, 10) : '');
    setPeriodTo(cir?.periodTo ? cir.periodTo.slice(0, 10) : '');
    setRemarks(cir?.remarks || '');
    setFiles([]);
    setBusy(false);
    setTried(false);
    setError(null);
    // Only when the dialog opens or shows another request: typing must not be reset by the page re-rendering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cir]);

  const own = useMemo(() => new Set((cir?.lines || []).map((l) => l.jobLineId)), [cir]);
  // Each job line is on one live request only: the open ones, and this request's own when editing.
  const choices = (job.lines || []).filter((l) => own.has(l.lineId) || (!l.cirNo && !l.invoiced));
  const subtotal = round2(choices.filter((l) => picked.includes(l.lineId)).reduce((s, l) => s + (Number(l.amount) || 0), 0));
  const vat = round2(subtotal * (Number(vatRate) || 0) / 100);
  const from = dateOrNull(periodFrom);
  const to = dateOrNull(periodTo);

  const problems: string[] = [];
  if (!picked.length) problems.push('Tick the job lines to invoice');
  if (periodFrom.trim() && !from) problems.push('The period start is not a date');
  if (periodTo.trim() && !to) problems.push('The period end is not a date');
  if (from && to && to < from) problems.push('The period ends before it starts');

  const toggle = (id: number) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const allPicked = choices.length > 0 && choices.every((l) => picked.includes(l.lineId));

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const chosen = Array.from(list);
    const big = chosen.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (big) { setError(`${big.name} is larger than 10 MB`); return; }
    setError(null);
    setFiles((fs) => [...fs, ...chosen]);
  };

  const save = async () => {
    setTried(true);
    if (problems.length) return;
    setBusy(true);
    setError(null);
    try {
      await onSave({
        customerTrn: trn.trim() || null,
        customerPoRef: poRef.trim() || null,
        periodFrom: from,
        periodTo: to,
        remarks: remarks.trim() || null,
        jobLineIds: choices.filter((l) => picked.includes(l.lineId)).map((l) => l.lineId),
      }, files);
    } catch (e: any) {
      setError(errorText(e, 'The invoice request could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth aria-labelledby="e2i-cir-title">
      <DialogTitle id="e2i-cir-title">{cir ? `Invoice request ${cir.cirNo || ''}`.trim() : `New invoice request for ${job.jobNo || 'the job'}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2" component="h3" sx={{ mr: 'auto' }}>Job lines to invoice</Typography>
              {choices.length > 1 ? (
                <Button size="small" variant="text" onClick={() => setPicked(allPicked ? [] : choices.map((l) => l.lineId))}>{allPicked ? 'Untick all' : 'Tick all'}</Button>
              ) : null}
            </Stack>
            {choices.length ? (
              <Stack component="ul" spacing={0} sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {choices.map((l) => (
                  <Box component="li" key={l.lineId} sx={{ borderBottom: '1px solid #eceff1' }}>
                    <FormControlLabel
                      sx={{ width: '100%', mr: 0, py: 0.25, '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 } }}
                      control={<Checkbox size="small" checked={picked.includes(l.lineId)} onChange={() => toggle(l.lineId)} />}
                      label={(
                        <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                          <Typography variant="body2" component="span" sx={{ flex: 1, minWidth: 0 }}>{l.lineNum}. {l.description}</Typography>
                          <Typography variant="body2" component="span" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>{formatAmount(l.amount)} {currency}</Typography>
                        </Box>
                      )}
                    />
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">Every line of this job is already on an invoice request.</Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'right' }}>
              {formatAmount(subtotal)} + VAT {formatAmount(vat)} = <strong>{formatAmount(round2(subtotal + vat))} {currency}</strong>
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                id="e2i-cir-trn"
                label="Customer VAT number"
                value={trn}
                onChange={(e) => setTrn(e.target.value.slice(0, 30))}
                fullWidth
                helperText={defaultTrn ? 'From the SAP customer' : 'SAP has none for this customer'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField id="e2i-cir-po" label="Client PO number" value={poRef} onChange={(e) => setPoRef(e.target.value.slice(0, 100))} fullWidth helperText="Printed on the invoice as the customer's reference" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField id="e2i-cir-from" type="date" label="Service from" value={periodFrom} onChange={(e) => setPeriodFrom(cleanDateInput(e.target.value))} InputLabelProps={{ shrink: true }} fullWidth />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField id="e2i-cir-to" type="date" label="Service to" value={periodTo} onChange={(e) => setPeriodTo(cleanDateInput(e.target.value))} InputLabelProps={{ shrink: true }} inputProps={{ min: from || undefined }} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField id="e2i-cir-remarks" label="Remarks for finance" value={remarks} onChange={(e) => setRemarks(e.target.value.slice(0, 2000))} multiline maxRows={4} fullWidth />
            </Grid>
          </Grid>
          {config.vatRegistered && !trn.trim() ? (
            <Alert severity="warning">Without the customer&apos;s VAT number finance cannot book this request.</Alert>
          ) : null}
          {!cir ? (
            <Box>
              <Button component="label" size="small" variant="outlined" startIcon={<AttachFileIcon />} disabled={busy}>
                Add supporting files
                <input hidden type="file" multiple accept={ATTACHMENT_ACCEPT} aria-label="Supporting files: photos or PDFs" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
              </Button>
              {files.length ? (
                <Stack component="ul" spacing={0.25} sx={{ m: 0, mt: 1, pl: 2.5 }}>
                  {files.map((f, i) => (
                    <Box component="li" key={`${f.name}-${i}`}>
                      <Typography variant="body2" component="span" sx={{ wordBreak: 'break-all' }}>{f.name}</Typography>
                      <Button size="small" variant="text" onClick={() => setFiles((fs) => fs.filter((_x, j) => j !== i))} aria-label={`Remove ${f.name}`}>Remove</Button>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>E.g. the completion certificate or the timesheet (photos or PDFs, 10 MB each).</Typography>
              )}
            </Box>
          ) : null}
          {tried && problems.length ? <Alert severity="warning">{problems.join('; ')}.</Alert> : null}
          {error ? <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => { void save(); }} disabled={busy}>{busy ? 'Saving…' : cir ? 'Save' : 'Create invoice request'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default CirDialog;
